import { ReactElement, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import { PAGE_ICONS } from 'pages/AnnualUserAccessReview/_const';
import { PAGE_ROUTES } from 'pages/AnnualUserAccessReview/_const';
import UarSubmitFlowOverlay from 'pages/AnnualUserAccessReview/components/UarSubmitFlowOverlay';
import PageWithBreadcrumbs from 'pages/AnnualUserAccessReview/layouts/UARPageWithBreadcrumbs';
import ReviewForm from './ReviewForm';
import * as uarActions from 'store/uar/actions';
import { selectProcessedRequests } from 'store/uar/selectors';

/**
 * View where manager can review and validate employee access
 * @returns {ReactElement} User Access Review Employee Review page view
 */
const ReviewView = ({ showActionFlowOverlay = false }) => {
	const dispatch = useDispatch();
	const processedRequests = useSelector(selectProcessedRequests);
	const history = useHistory();
	const location = useLocation();
	const [sShowCloseConfirmation, setShowCloseConfirmation] = useState(false);
	const [sOverlayContainerElement, setOverlayContainerElement] = useState(null);

	const searchParams = new URLSearchParams(location.search || '');
	const view = searchParams.get('view');
	const overlayMode = view === 'processed' ? 'processed' : 'modify';
	const processedUserIds = Array.from(
		new Set(
			processedRequests
				.map(entry => Number(entry?.staffMemberId))
				.filter(id => Number.isFinite(id) && id > 0),
		),
	);

	const onCompletedFlowExitHandler = useCallback(() => {
		dispatch(uarActions.clearActionCart());
		dispatch(uarActions.clearProcessedRequests());
		dispatch(uarActions.resetQueue());

		if (processedUserIds.length > 0) {
			const reviewParams = new URLSearchParams({
				esoFiId: processedUserIds.join(','),
			});
			history.replace(`${PAGE_ROUTES.REVIEW}?${reviewParams.toString()}`);
			return;
		}

		history.replace(PAGE_ROUTES.REVIEW);
	}, [dispatch, history, processedUserIds]);

	const onRequestCloseHandler = () => {
		setShowCloseConfirmation(true);
	};

	const onCancelCloseHandler = () => {
		setShowCloseConfirmation(false);
	};

	const onConfirmCloseHandler = () => {
		setShowCloseConfirmation(false);

		if (overlayMode === 'processed') {
			onCompletedFlowExitHandler();
		} else {
			dispatch(uarActions.clearProcessedRequests());
			dispatch(uarActions.resetQueue());
			history.push(PAGE_ROUTES.REVIEW);
		}
	};

	const onProcessedDoneHandler = useCallback(() => {
		onCompletedFlowExitHandler();
	}, [onCompletedFlowExitHandler]);

	const onSetOverlayContainerElementHandler = useCallback(node => {
		if (node) {
			setOverlayContainerElement(node);
		}
	}, []);

	return (
		<PageWithBreadcrumbs
			currentPageLabel={'Review Employee Access'}
			CurrentPageIcon={PAGE_ICONS.REVIEW}
		>
			<Box
				ref={onSetOverlayContainerElementHandler}
				sx={{ position: 'relative', minHeight: 'calc(100vh - 220px)' }}
			>
				<ReviewForm />
				{showActionFlowOverlay ? (
					<UarSubmitFlowOverlay
						open
						container={sOverlayContainerElement}
						mode={overlayMode}
						onRequestClose={onRequestCloseHandler}
						onConfirmClose={onConfirmCloseHandler}
						onCancelClose={onCancelCloseHandler}
						showCloseConfirmation={sShowCloseConfirmation}
						onProcessedDone={onProcessedDoneHandler}
					/>
				) : null}
			</Box>
		</PageWithBreadcrumbs>
	);
};

export default ReviewView;